import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Button, Card, ProgressBar, Form } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { FaTimes } from "react-icons/fa";
import KpiStat from "../utils/KpiStat";
import Loading from "../../../../../components/general/Loading";
import { getIndicatorCaes } from "../../../../../rest/indicator";
import {
  handleError,
  isNotBusinessError,
} from "../../../../../utils/handleError";
import ExportDialog from "../utils/ExportDialog";
import LastUpdate from "../utils/LastUpdate";
import { buildWorkbookCae } from "../sectionsIndicatorsCandidatures/exporters/exporterIndicatorCAE";
import * as XLSX from "xlsx";
import { useClientPdf } from "../utils/useClientPdf";

export default function IndicatorCAE() {
  const intl = useIntl();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [query, setQuery] = useState("");
  const [showExport, setShowExport] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(null);

  const { pdfRef, exportPdf } = useClientPdf({
    marginMm: 10,
    orientation: "portrait",
    hideSelectors: ['[data-hide-on-export="true"]', ".modal", ".toast"],
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await getIndicatorCaes();
        const payload = resp?.data || {};
        setData(payload.data || []);
        setLastUpdate(payload.lastRefreshedAt || null);
        setRefreshVersion(payload.refreshVersion || null);
      } catch (err) {
        if (isNotBusinessError(err)) setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const normalize = (s = "") =>
    s
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const filtered = useMemo(() => {
    const tokens = normalize(query)
      .split(/[\s,;]+/)
      .filter(Boolean);
    if (tokens.length === 0) return data;
    return data.filter((item) => {
      const codeStr = String(item?.code ?? "");
      const codeNorm = normalize(codeStr);
      const descNorm = normalize(item?.description ?? "");
      return tokens.every((tok) => {
        const isNumeric = /^\d+$/.test(tok);
        if (isNumeric) return codeStr.startsWith(tok) || descNorm.includes(tok);
        return descNorm.includes(tok) || codeNorm.includes(tok);
      });
    });
  }, [data, query]);

  const sumField = (arr, field) =>
    arr.reduce((sum, item) => sum + (item?.[field] || 0), 0);
  const totalAll = useMemo(() => sumField(filtered, "counter"), [filtered]);

  const STATE_DEFS = [
    {
      key: "sumApproved",
      id: "groupState.3",
      label: "Aprovadas",
      variant: "success",
    },
    {
      key: "sumEligibility",
      id: "groupState.2",
      label: "Por tratar",
      variant: "secondary",
    },
    {
      key: "sumOthers",
      id: "groupState.7",
      label: "Em tratamento",
      variant: "info",
    },
    {
      key: "sumRevocation",
      id: "groupState.4",
      label: "Revogação",
      variant: "warning",
    },
    {
      key: "sumRejected",
      id: "groupState.5",
      label: "Indeferidas",
      variant: "danger",
    },
  ];

  const totalsByState = useMemo(
    () =>
      STATE_DEFS.map((s) => ({
        ...s,
        value: sumField(filtered, s.key),
      })),
    [filtered]
  );

  const sortedList = useMemo(
    () => filtered.slice().sort((a, b) => b.counter - a.counter),
    [filtered]
  );

  const toPct = (v, t) => (t > 0 ? Math.round((v / t) * 100) : 0);

  const handleExportExcelFinal = async () => {
    try {
      const wb = await buildWorkbookCae();
      XLSX.writeFile(wb, "indicadores_cae.xlsx");
      setShowExport(false);
    } catch (e) {
      console.error("[Excel CAE] falhou:", e);
      alert("Falha ao gerar Excel: " + (e?.message || e));
    }
  };

  const handleExportPdfClient = async () => {
    try {
      await exportPdf("indicadores_cae.pdf");
      setShowExport(false);
    } catch (e) {
      console.error("[PDF CAE] falhou:", e);
      alert("Falha ao gerar PDF: " + (e?.message || e));
    }
  };

  if (loading) return <Loading />;
  if (error) return handleError(error);

  return (
    <div className="container-fluid" ref={pdfRef}>
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="text-secondary mb-0">
            <FormattedMessage
              id="indicator.candidatures.byCae"
              defaultMessage="Candidaturas por CAE"
            />
          </h4>
          <small className="text-muted d-block">
            Este indicador contabiliza candidaturas.
          </small>
          <small className="text-muted">
            O total pode ser inferior ao total global da Medida quando existem candidaturas sem CAE preenchido.
          </small>
        </Col>
        <Col className="text-end">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowExport(true);
            }}
            style={{ color: "#e5533d", textDecoration: "underline" }}
            data-hide-on-export="true"
          >
            <FormattedMessage
              id="indicator.exportar"
              defaultMessage="Exportar"
            />
          </a>
        </Col>
      </Row>

      <Row className="g-3 align-items-stretch mb-3">
        <Col xs={6} md={3} lg={2}>
          <KpiStat
            label={intl.formatMessage({
              id: "indicator.total",
              defaultMessage: "Total",
            })}
            value={totalAll}
          />
        </Col>

        <Col xs={6} md={3} lg={2}>
          <KpiStat
            label={intl.formatMessage({
              id: "indicator.distinct.cae",
              defaultMessage: "CAE distintos",
            })}
            value={filtered.length}
          />
        </Col>

        <Col xs={12} md={6} lg={8}>
          <Card className="shadow-sm h-100">
            <Card.Body className="py-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="text-secondary mb-0">
                  <FormattedMessage
                    id="indicator.status.distribution"
                    defaultMessage="Distribuição por Estado (global)"
                  />
                </h6>
                <div className="text-muted">
                  <FormattedMessage
                    id="indicator.total"
                    defaultMessage="Total"
                  />
                  : <strong>{totalAll}</strong>
                </div>
              </div>

              {(() => {
                const parts = totalsByState.filter((s) => s.value > 0);
                let acc = 0;
                return (
                  <ProgressBar style={{ height: 12 }}>
                    {parts.map((s, idx) => {
                      const isLast = idx === parts.length - 1;
                      const pctExact =
                        totalAll > 0 ? (s.value / totalAll) * 100 : 0;
                      const pct = isLast
                        ? Math.max(0, 100 - acc)
                        : +pctExact.toFixed(4);
                      if (!isLast) acc += pct;
                      const displayPct = Math.round(pctExact);
                      const label = intl.formatMessage({
                        id: s.id,
                        defaultMessage: s.label,
                      });
                      return (
                        <ProgressBar
                          key={s.key}
                          now={pct}
                          variant={s.variant}
                          title={`${label}: ${s.value} (${displayPct}%)`}
                        />
                      );
                    })}
                  </ProgressBar>
                );
              })()}

              <div className="d-flex flex-wrap gap-2 mt-2">
                {totalsByState.map((s) => {
                  const displayPct = toPct(s.value, totalAll);
                  return (
                    <div key={s.key} className="d-flex align-items-center me-2">
                      <span
                        className={`d-inline-block rounded me-1 bg-${s.variant}`}
                        style={{ width: 10, height: 10 }}
                      />
                      <span className="text-nowrap">
                        <FormattedMessage id={s.id} defaultMessage={s.label} />:{" "}
                        {s.value} ({displayPct}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="align-items-end g-3 mb-1">
        <Col xs={12} md={6} lg={5}>
          <div className="d-flex gap-2">
            <Form.Control
              value={query}
              placeholder="Pesquisar por CAE"
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <Button
                variant="outline-secondary"
                onClick={() => setQuery("")}
                title="Limpar"
              >
                <FaTimes />
              </Button>
            )}
          </div>
        </Col>
      </Row>

      <Card className="mb-3 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="text-secondary mb-0">
              <FormattedMessage
                id="indicator.all.cae"
                defaultMessage="Candidaturas por CAE"
              />
            </h6>
            <div className="text-muted">
              <FormattedMessage id="indicator.total" defaultMessage="Total" />:{" "}
              <strong>{totalAll}</strong>
            </div>
          </div>

          {sortedList.length === 0 && (
            <div className="text-muted">Sem dados</div>
          )}

          {sortedList.map((item) => {
            const pct = toPct(item.counter, totalAll);
            return (
              <div key={item.code} className="mb-2">
                <div className="d-flex justify-content-between">
                  <div
                    className="text-truncate"
                    title={`${item.code} - ${item.description}`}
                  >
                    {item.code} - {item.description}
                  </div>
                  <div className="ms-2">
                    <strong>{item.counter}</strong>{" "}
                    <span className="text-muted">({pct}%)</span>
                  </div>
                </div>
                <ProgressBar now={pct} style={{ height: 8 }} />
              </div>
            );
          })}
        </Card.Body>
      </Card>

      <LastUpdate iso={lastUpdate} version={refreshVersion} className="mt-3" />

      <ExportDialog
        show={showExport}
        onHide={() => setShowExport(false)}
        options={[
          { onClick: handleExportExcelFinal, filename: "indicadores_cae.xlsx" },
          {
            onClick: () => exportPdf("indicadores_cae.pdf"),
            filename: "indicadores_cae.pdf",
          },
        ]}
      />
    </div>
  );
}